import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CalendarDays, GitBranch, ListChecks, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TASK_PRIORITY_COLOR, TASK_PRIORITY_LABEL, type Task } from '@/../../shared/internal-tasks';
import { Dot, fmtDueDate, isOverdue, shortName } from './task-fields';

/** Tarea raíz enriquecida con datos del proyecto para pintar la card. */
export type BoardCard = Task & {
  projectName: string;
  projectColor: string;
  subtaskCount: number;
  subtaskDone: number;
};

export function SortableCard({ card, onOpen }: { card: BoardCard; onOpen: (taskId: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.taskId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      onClick={() => onOpen(card.taskId)}
      className={cn('cursor-grab active:cursor-grabbing', isDragging && 'opacity-40')}
    >
      <TaskCardBody card={card} />
    </div>
  );
}

export function TaskCardBody({ card, dragging }: { card: BoardCard; dragging?: boolean }) {
  const overdue = isOverdue(card);
  const checklistTotal = card.checklist?.length ?? 0;
  const checklistDone = card.checklist?.filter((c) => c.isCompleted).length ?? 0;
  return (
    <div className={cn('flex flex-col gap-2 rounded-lg border bg-card p-3 shadow-xs', dragging && 'rotate-2 shadow-lg')}>
      <div className="flex items-center gap-1.5">
        <Dot color={card.projectColor} className="h-2 w-2" />
        <span className="truncate text-xs text-muted-foreground">{card.projectName}</span>
      </div>
      <p className={cn('text-sm font-medium leading-snug', card.status === 'completed' && 'text-muted-foreground line-through')}>
        {card.title}
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1" title={`Prioridad ${TASK_PRIORITY_LABEL[card.priority].toLowerCase()}`}>
          <Dot color={TASK_PRIORITY_COLOR[card.priority]} className="h-2 w-2" />
        </span>
        {card.dueDate && (
          <span className={cn('flex items-center gap-1', overdue && 'font-medium text-destructive')}>
            <CalendarDays className="h-3 w-3" />
            {fmtDueDate(card.dueDate)}
          </span>
        )}
        {card.subtaskCount > 0 && (
          <span className="flex items-center gap-1" title="Subtareas">
            <GitBranch className="h-3 w-3" />
            {card.subtaskDone}/{card.subtaskCount}
          </span>
        )}
        {checklistTotal > 0 && (
          <span className="flex items-center gap-1" title="Checklist">
            <ListChecks className="h-3 w-3" />
            {checklistDone}/{checklistTotal}
          </span>
        )}
        {card.assigneeEmail && (
          <span className="ml-auto flex items-center gap-1 truncate" title={card.assigneeEmail}>
            <User className="h-3 w-3" />
            {shortName(card.assigneeEmail)}
          </span>
        )}
      </div>
    </div>
  );
}
